import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

// eslint-disable-next-line no-restricted-globals
const logsBaseUrl = new URL('/api/action_log', location)

// Step 1: Create Context (default value is optional)
const ActionLogsContext = createContext()

// Step 2: Create Provider Component
export const ActionLogsProvider = ({ children }) => {
  const location = useLocation() 
  const navigate = useNavigate()
  const token = location.state?.token
  const init = location.state?.init
  const [logs, setLogs] = useState(null)
  const [count, setCount] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  const fetchLogs = useCallback(async params => {
    try {
      const url = new URL(logsBaseUrl)
      url.searchParams.set('limit', 500)

      if (params && params.length) {
        params.forEach(([k, v]) => {
          url.searchParams.append(k, v)
        })
      }

      const { logs } = await (await fetch(url, {
        headers: {
          'content-type'   : 'application/json'
          , accept         : 'application/json'
          , authorization  : `Bearer ${token}`
        }
      })).json()
      setLogs(logs)

      const count = logs.length - logs.map(log => log.status).reduce((a, b) => a + b, 0)
      setCount(count)
    } catch (error) {
      console.error('Failed to fetch count:', error)
      setLogs([])
      setCount(0) // Fallback
    } finally {
      setIsLoading(false)
    }
  }, [token])

  // Fetch initial count from API on mount
  useEffect(() => {
    if (token && init.user.type === 1) fetchLogs()
  }, [token, init?.user.type, navigate, fetchLogs])

  // Update function (can be called from nested components)
  const setAsRead = id => {
    // Sync with API on update
    fetch(logsBaseUrl
      , {
        method: 'POST'
        , headers: {
          'content-type'   : 'application/json'
          , accept         : 'application/json'
          , authorization  : `Bearer ${token}`
        }
        , body: JSON.stringify({ id })
      })
      .then(res => {
        if (res.ok) {
          setCount(count - 1)
          setLogs(old => old.map(log => log.id === id ? { ...log, status: 1 } : log))
        } else throw new Error('Read update error')
      })
      .catch(error => console.error(error))
  }

  return (
    <ActionLogsContext.Provider value={{ logs, count, setAsRead, isLoading, fetchLogs }}>
      {children}
    </ActionLogsContext.Provider>
  )
}

// Step 3: Custom Hook for easy access
export const useActionLogs = () => {
  const context = useContext(ActionLogsContext)
  if (!context) throw new Error('useCount must be used within a NewLogsProvider')
  return context // Returns { count, setCount }
}

